/**
 * FACE ID AGENTI (SATA Agent ilovasi) — maktab FACE ID sahifasi va superadmin "FACE ID agentlar"
 * sahifasi uchun umumiy turlar va SOF yordamchilar. Bu faylda React/i18n importi YO'Q:
 * `scripts/test-agent.ts` uni to'g'ridan-to'g'ri Node'da sinaydi (`npm test`).
 * Backend: `lib/agentBoshqaruv.ts` (spec docs/superpowers/specs/2026-10-15-faceid-agent-boshqaruv-design.md §6).
 */

export type UmumiyHolat =
  | 'toxtatilgan' | 'kalitXato' | 'serverYoq' | 'terminalYoq' | 'kutilmoqda' | 'joyida' | 'terminalSozlanmagan';
export type TerminalHolat = 'nomalum' | 'korinadi' | 'korinmaydi' | 'sozlanmagan';

/**
 * Ilovaning holat paketi (`POST /faceid/agent/holat` tanasi). Ichidagi vaqtlar (`ishgaTushgan`,
 * `oxirgiAloqa`, `oxirgiXatoAt`) KOMPYUTER soati bo'yicha — faqat ko'rsatish uchun, hisob uchun emas.
 */
export interface HolatPaketi {
  umumiy: UmumiyHolat;
  ishlayapti: boolean;
  terminal: TerminalHolat;
  terminalManzil: string | null;
  oxirgiAloqa: string | null;
  oxirgiXato: string | null;
  oxirgiXatoAt: string | null;
  /** Server yaroqsiz sanani / bo'sh qiymatni null qiladi. */
  ishgaTushgan: string | null;
  os: string | null;
  band: boolean;
}

/** `GET /faceid/terminals` → `agents[]` elementi (vaqtlar SERVER soati bo'yicha). */
export interface AgentQator {
  id: string;
  name: string;
  version: string | null;
  ip: string | null;
  reachable: boolean | null;
  lastSeenAt: string;
  /** null — boshqaruv sikli hech qachon kelmagan (ilova ≤ 0.1.2 yoki eski Node agent). */
  holat: HolatPaketi | null;
  holatAt: string | null;
  pauza: boolean;
  sogAt: string | null;
}

/** Superadmin ro'yxati (`GET /superadmin/agentlar`) elementi. */
export interface AgentRoyxatQatori extends AgentQator {
  terminal: { id: string; name: string };
  tenant: { id: string; name: string } | null;
}

export type BuyruqTur = 'QAYTA_ISHGA_TUSHIR' | 'LOG' | 'YANGILA' | 'TOXTAT' | 'DAVOM';
export type BuyruqHolat = 'KUTILMOQDA' | 'YUBORILDI' | 'BAJARILDI' | 'XATO' | 'MUDDATI_OTDI';

export interface AgentBuyruq {
  id: string;
  tur: BuyruqTur;
  holat: BuyruqHolat;
  natija: string | null;
  logBor: boolean;
  kim: string | null;
  createdAt: string;
  yuborildiAt: string | null;
  tugadiAt: string | null;
}

export interface AgentVazifa {
  id: string;
  kind: string;
  status: string;
  error: string | null;
  result: unknown;
  createdAt: string;
  finishedAt: string | null;
}

/** `GET /faceid/agents/:id` va `GET /superadmin/agentlar/:id` javobi. */
export interface AgentBatafsil {
  agent: AgentQator & { terminal: { id: string; name: string }; tenant?: { id: string; name: string } | null };
  /** Oxirgi 10 ta, yangisi birinchi. */
  vazifalar: AgentVazifa[];
  /** Oxirgi 20 ta, yangisi birinchi. */
  buyruqlar: AgentBuyruq[];
}

/** Batafsil oyna manzillari: maktab va superadmin endpointlari bir xil javob beradi. */
export interface AgentYollari {
  batafsil: (id: string) => string;
  buyruq: (id: string) => string;
  log: (buyruqId: string) => string;
}

export const MAKTAB_AGENT_YOLLARI: AgentYollari = {
  batafsil: (id) => `/faceid/agents/${id}`,
  buyruq: (id) => `/faceid/agents/${id}/buyruq`,
  log: (buyruqId) => `/faceid/agent-buyruq/${buyruqId}/log`,
};

export const SUPERADMIN_AGENT_YOLLARI: AgentYollari = {
  batafsil: (id) => `/superadmin/agentlar/${id}`,
  buyruq: (id) => `/superadmin/agentlar/${id}/buyruq`,
  log: (buyruqId) => `/superadmin/agent-buyruq/${buyruqId}/log`,
};

export type AgentHolatKalit =
  | 'eskiVersiya' | 'aloqaYoq' | 'toxtatilgan' | 'ishlayapti' | 'terminalYoq'
  | 'terminalSozlanmagan' | 'serverYoq' | 'kalitXato' | 'tekshirilmoqda';

export interface AgentHolati {
  kalit: AgentHolatKalit;
  /** Mantine rangi (Badge `color`). */
  rang: string;
  /** Faqat `aloqaYoq` (boshqaruv sikli bor agent): `holatAt` dan beri o'tgan to'liq daqiqalar. */
  daqiqa?: number;
}

/** Eski ilova / Node agent: `lastSeenAt` shu oraliqda bo'lsa "Eski versiya", aks holda "Aloqa yo'q". */
export const ESKI_ONLAYN_MS = 2 * 60_000;

/**
 * `holatAt` shundan eski bo'lsa — "Aloqa yo'q" (spec §6.1: 2 daqiqa). Server holatni faqat o'zgarganda
 * yoki 60 s da bir yozadi (`HOLAT_YOZUV_MS`) va ilova har 15 s yuboradi: sog' kompyuterning `holatAt` i
 * 75 s gacha eskiradi (+ ro'yxat 30 s da yangilanadi) — 2 daqiqa chegara soxta "Aloqa yo'q" chaqnashini
 * bermaydi (eski agentlar chegarasi bilan bir xil).
 */
export const ALOQA_CHEGARA_MS = 2 * 60_000;

/** "Logni olish" bosilgach batafsil 2 s da yangilanadigan eng uzoq vaqt (spec §6.2). */
export const LOG_KUTISH_MS = 60_000;

/**
 * Server soati − brauzer soati (ms). `serverVaqti` — `GET /health` javobidagi `timestamp`;
 * `boshi` / `oxiri` — so'rov yuborilgan va javob kelgan payt (brauzer soati); server vaqti so'rov
 * o'rtasiga to'g'ri keladi deb olinadi. Yaroqsiz javob → 0 (brauzer soati).
 * Panel `agentHolati` ga `Date.now() + farq` beradi (`hooks/useServerSoati`): panel ochilgan maktab
 * kompyuterining soati noto'g'ri bo'lsa ham "Aloqa yo'q" server vaqti bo'yicha (Review Focus 2).
 */
export function soatFarqi(serverVaqti: unknown, boshi: number, oxiri: number): number {
  const server = typeof serverVaqti === 'string' ? Date.parse(serverVaqti) : NaN;
  if (!Number.isFinite(server)) return 0;
  return Math.round(server - (boshi + oxiri) / 2);
}

/**
 * Panel "hozir" i (brauzer soati, ms; server farqi `useServerSoati` da qo'shiladi): oxirgi tik va agentlar ro'yxati
 * kelgan payt (react-query `dataUpdatedAt`; ma'lumot hali yo'q — 0) dan kechrog'i. Fondan qaytilganda tik muzlagan
 * bo'ladi: yangi ro'yxat kelishi bilan "hozir" ham o'sha paytga suriladi (fonda o'chgan kompyuter tikni kutmasdan
 * "Aloqa yo'q"), u kelguncha esa eski ro'yxat eski "hozir" bilan baholanadi (sog' kompyuter yolg'on qizarmaydi).
 */
export function panelHozir(tik: number, malumotVaqti: number): number {
  return Math.max(tik, malumotVaqti);
}

/**
 * Agent holat belgisi — panelda BITTA joy (spec §6.1). Tartib muhim.
 * "Aloqa yo'q" FAQAT serverning `holatAt` i bo'yicha: paketdagi vaqtlar kompyuter soatidan va
 * noto'g'ri bo'lishi mumkin (Review Focus 2).
 */
export function agentHolati(
  agent: Pick<AgentQator, 'lastSeenAt' | 'holat' | 'holatAt' | 'pauza'>,
  hozir: number
): AgentHolati {
  // 1. Boshqaruv sikli hech qachon kelmagan — eski ilova (≤ 0.1.2) yoki Node agent
  if (!agent.holatAt || !agent.holat) {
    return hozir - Date.parse(agent.lastSeenAt) < ESKI_ONLAYN_MS
      ? { kalit: 'eskiVersiya', rang: 'gray' }
      : { kalit: 'aloqaYoq', rang: 'red' };
  }
  // 2. Boshqaruv paketi kelmay qo'ygan
  const otdi = hozir - Date.parse(agent.holatAt);
  if (otdi > ALOQA_CHEGARA_MS) return { kalit: 'aloqaYoq', rang: 'red', daqiqa: Math.floor(otdi / 60_000) };
  // 3. Terminal sikli to'xtatilgan (paneldan yoki ilova oynasidan)
  if (agent.pauza) return { kalit: 'toxtatilgan', rang: 'yellow' };
  // 4. Ilovaning umumiy holati
  switch (agent.holat.umumiy) {
    case 'joyida': return { kalit: 'ishlayapti', rang: 'teal' };
    case 'terminalYoq': return { kalit: 'terminalYoq', rang: 'orange' };
    case 'terminalSozlanmagan': return { kalit: 'terminalSozlanmagan', rang: 'gray' };
    case 'serverYoq': return { kalit: 'serverYoq', rang: 'red' };
    case 'kalitXato': return { kalit: 'kalitXato', rang: 'red' };
    case 'toxtatilgan': return { kalit: 'toxtatilgan', rang: 'yellow' };
    // `kutilmoqda` va kelajakdagi noma'lum qiymat
    default: return { kalit: 'tekshirilmoqda', rang: 'blue' };
  }
}

/** Tarjima funksiyasi — komponentdan `(k, o) => t(k, o)` bilan beriladi (fayl i18n'ga bog'lanmasin). */
export type Tarjima = (kalit: string, opts?: { n: number }) => string;

/** Belgi matni: "Ishlayapti", "Aloqa yo'q — 5 daq", "Aloqa yo'q — 3 soat", "Aloqa yo'q — 2 kun". */
export function holatMatni(h: AgentHolati, t: Tarjima): string {
  const nom = t(`agent.holat.${h.kalit}`);
  if (h.daqiqa === undefined) return nom;
  const d = h.daqiqa;
  const davomiylik = d < 60
    ? t('agent.davomiylik.daq', { n: d })
    : d < 24 * 60
      ? t('agent.davomiylik.soat', { n: Math.floor(d / 60) })
      : t('agent.davomiylik.kun', { n: Math.floor(d / (24 * 60)) });
  return `${nom} — ${davomiylik}`;
}

/** Ishlamayotgan kompyuterlar — superadmin ro'yxatida tepada (spec §6.3). */
export const MUAMMOLI: AgentHolatKalit[] = ['aloqaYoq', 'toxtatilgan', 'terminalYoq', 'kalitXato', 'serverYoq'];

/** Oxirgi aloqa (server vaqti): `holatAt` va `lastSeenAt` dan kechrog'i. */
export function oxirgiAloqa(agent: Pick<AgentQator, 'lastSeenAt' | 'holatAt'>): string {
  if (!agent.holatAt) return agent.lastSeenAt;
  return Date.parse(agent.holatAt) > Date.parse(agent.lastSeenAt) ? agent.holatAt : agent.lastSeenAt;
}

/** Muammolilar tepada; guruh ichida oxirgi aloqasi yangirog'i birinchi. Asl massiv o'zgarmaydi. */
export function agentlarniSarala<T extends AgentQator>(agentlar: T[], hozir: number): T[] {
  const daraja = (a: T) => (MUAMMOLI.includes(agentHolati(a, hozir).kalit) ? 0 : 1);
  return [...agentlar].sort(
    (a, b) => daraja(a) - daraja(b) || Date.parse(oxirgiAloqa(b)) - Date.parse(oxirgiAloqa(a))
  );
}

export const BUYRUQ_RANG: Record<BuyruqHolat, string> = {
  KUTILMOQDA: 'gray',
  YUBORILDI: 'blue',
  BAJARILDI: 'teal',
  XATO: 'red',
  MUDDATI_OTDI: 'orange',
};

export function buyruqTugaganmi(holat: BuyruqHolat): boolean {
  return holat === 'BAJARILDI' || holat === 'XATO' || holat === 'MUDDATI_OTDI';
}

/** Ko'rsatiladigan log — logi bor eng yangi `LOG` buyrug'i (ro'yxat yangisi birinchi). */
export function oxirgiLogBuyrugi(buyruqlar: AgentBuyruq[]): string | null {
  return buyruqlar.find((b) => b.tur === 'LOG' && b.logBor)?.id ?? null;
}

/** "Logni olish" buyrug'i hali tugamaganmi. Ro'yxatda hali ko'rinmagan (yangi yaratilgan) — kutilmoqda. */
export function logKutilmoqdami(buyruqlar: AgentBuyruq[], buyruqId: string): boolean {
  const b = buyruqlar.find((x) => x.id === buyruqId);
  return !b || !buyruqTugaganmi(b.holat);
}

/** Vazifa natijasi / xato matnini jadval uchun qisqartiradi (to'liq matn tooltipda). */
export function qisqaMatn(v: unknown, max: number): string {
  if (v === null || v === undefined || v === '') return '';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Server javobidagi bayroq → i18n kaliti; null — serverning `error` matni, u bo'lmasa umumiy xato (`eskiAgent` ham, spec §6.2). */
export function agentXatoKaliti(d?: Record<string, unknown> | null): string | null {
  if (d?.takror) return 'agent.xTakror';
  return null;
}
